import { useEffect, useState } from 'react';
import { AlertCircle, Plus, Trash2, UserPlus, Save, Lock, Globe } from 'lucide-react';
import type { Influencer, Location } from '../../../shared/types';
import { pb, errorText } from '../lib/pb';
import CreatorModal from './CreatorModal';
import FieldHint from './FieldHint';
import ThumbWithPreview from './ThumbPreview';

/**
 * Create — or, given `existing`, edit — an avatar (influencer) without
 * touching the Admin tab. Editing is open to the avatar's creator and admins
 * (the server enforces the same rule); deleting stays admin-only.
 *
 * Everything the pipeline needs is collected here: identity, the two "lock"
 * descriptions the image prompts are built from, the script persona, and at
 * least four locations (rooms) — images can't render without rooms to stand in.
 *
 * Image links accept Google Drive share links OR any direct image URL (a
 * bucket link, a CDN link) — the pipeline treats non-Drive URLs as-is.
 */

const MIN_LOCATIONS = 4;

interface LocationRow {
  /** Set when the row already exists in the database (edit mode). */
  id?: string;
  name: string;
  tag?: string;
  url: string;
  /** Optional "when to use this room" hint shown to the script writer. */
  hint?: string;
}

/** `Kitchen Counter` → `kitchen_counter`, matching the tags v1 used. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function looksLikeUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

const HINTS = {
  name: {
    what: 'The avatar’s display name — how they appear in the campaign pickers and history.',
    example: 'Yang',
  },
  gender: {
    what: 'Drives the pronouns used in every image and video prompt ("she holds the bottle" vs "he holds the bottle"). Getting this wrong produces mixed-up descriptions.',
  },
  baseImage: {
    what: 'The reference photo the image model copies the face and body from. Use a sharp, well-lit, front-facing photo of the person from roughly the waist up, on a simple background. Every generated image starts from this, so quality here decides quality everywhere. Drive link or direct image link.',
    example: 'https://drive.google.com/file/d/1AbC…/view  —  or  https://your-bucket.com/avatars/yang-reference.jpg',
  },
  avatarThumb: {
    what: 'A small square-ish photo shown next to the name in lists and dropdowns. Can be the same link as the character photo. Drive link or direct image link.',
    example: 'https://your-bucket.com/avatars/yang-thumb.jpg',
  },
  characterLock: {
    what: 'The fixed physical description pasted into every image prompt so the person looks the same in every scene. Describe what never changes: age range, hair, build, complexion, outfit. No feelings, no actions — just appearance.',
    example:
      'An East Asian woman in her early 30s with shoulder-length straight black hair, warm brown eyes and a natural, friendly smile. She wears a plain sand-colored knit sweater. Natural makeup, no jewelry.',
  },
  settingLock: {
    what: 'The fixed environment description, also pasted into every image prompt. Describe the overall home/style the rooms share, so scenes feel like one place. The specific room comes from the location list below.',
    example:
      'A bright, modern Scandinavian apartment with warm natural light, light wood surfaces, white walls and a few green plants.',
  },
  prompt: {
    what: 'The persona used when the AI writes scripts as this person: who they are, how they talk, what they care about. The script writer speaks in this voice.',
    example:
      'You are Yang, a 32-year-old wellness enthusiast and busy marketing professional. You speak casually and warmly, like recommending something to a close friend. You are practical and slightly skeptical — you only recommend things you actually use, and you like concrete everyday details over hype.',
  },
  guidelines: {
    what: 'Dos and don’ts for scripts with this avatar: tone, hook examples, competitor reference scripts. The script writer follows these on every campaign — the universal rules (JSON format, locations, scene length, camera work) are always applied automatically and don’t need repeating here.',
    example:
      'Always speak from personal experience ("I noticed…", "for me…"). Keep sentences short and spoken-sounding. Never make medical claims or promise results. Mention the product by name exactly once in the first half.',
  },
  locations: {
    what: 'The rooms this avatar can appear in. Each needs a photo of the actual room — the image model places the person into it. Use clean, well-lit photos with no people in them. At least 4, so scenes can vary. Drive links or direct image links.',
    example: 'Kitchen counter  →  https://your-bucket.com/rooms/yang-kitchen.jpg',
  },
  privacy: {
    what: 'Public avatars are visible to the whole team. A private avatar is visible only to the people you list here (and admins). People must have signed into the app at least once before their email can be added.',
  },
} as const;

export default function AvatarCreator({
  userId,
  existing,
  onClose,
  onCreated,
}: {
  userId: string;
  /** When set, the dialog edits this avatar instead of creating a new one. */
  existing?: Influencer;
  onClose: () => void;
  /** Called with the influencer's name after everything is saved. */
  onCreated: (name: string) => void;
}) {
  const editing = Boolean(existing);
  const [name, setName] = useState(existing?.name ?? '');
  const [gender, setGender] = useState<'female' | 'male'>(
    existing?.gender === 'male' ? 'male' : 'female'
  );
  const [baseImageUrl, setBaseImageUrl] = useState(existing?.base_image_url ?? '');
  const [avatarUrl, setAvatarUrl] = useState(existing?.avatar_url ?? '');
  const [characterLock, setCharacterLock] = useState(existing?.character_lock ?? '');
  const [settingLock, setSettingLock] = useState(existing?.setting_lock ?? '');
  const [prompt, setPrompt] = useState(existing?.prompt ?? '');
  const [guidelines, setGuidelines] = useState(existing?.guidelines ?? '');
  const [locations, setLocations] = useState<LocationRow[]>(
    Array.from({ length: MIN_LOCATIONS }, () => ({ name: '', url: '' }))
  );
  /** Existing rows removed in the editor — deleted from the DB on save. */
  const [removedLocationIds, setRemovedLocationIds] = useState<string[]>([]);
  const [isPrivate, setIsPrivate] = useState(Boolean(existing?.allowed_users?.length));
  const [emailsText, setEmailsText] = useState('');
  const [prefilling, setPrefilling] = useState(editing);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit mode: pull the avatar's current locations and, for a private avatar,
  // the emails behind the allowed_users ids, so the form shows reality.
  useEffect(() => {
    if (!existing) return;
    let cancelled = false;
    void (async () => {
      try {
        const rooms = await pb.collection('locations').getFullList({
          filter: `influencer = "${existing.id}"`,
          sort: 'sort',
        });
        if (cancelled) return;
        const rows: LocationRow[] = (rooms as unknown as Location[]).map((l) => ({
          id: l.id,
          name: l.name,
          tag: l.tag,
          url: l.drive_url,
          hint: l.usage_hint ?? '',
        }));
        while (rows.length < MIN_LOCATIONS) rows.push({ name: '', url: '' });
        setLocations(rows);

        if (existing.allowed_users?.length) {
          const filter = existing.allowed_users.map((id) => `id = "${id}"`).join(' || ');
          const users = await pb.collection('users').getFullList({ filter });
          if (cancelled) return;
          setEmailsText(
            (users as Array<{ email?: string }>).map((u) => u.email).filter(Boolean).join(', ')
          );
        }
      } catch (err) {
        if (!cancelled) setError(errorText(err));
      } finally {
        if (!cancelled) setPrefilling(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing]);

  // Partial-failure recovery: if the influencer was created but a location
  // write failed, a retry continues from where it stopped instead of creating
  // a duplicate avatar.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [savedLocations, setSavedLocations] = useState<Set<number>>(new Set());

  const setLocation = (index: number, patch: Partial<LocationRow>) =>
    setLocations((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const filledLocations = locations.filter((l) => l.name.trim() && l.url.trim());

  const problems: string[] = [];
  if (!name.trim()) problems.push('a name');
  if (!looksLikeUrl(baseImageUrl)) problems.push('a character photo link');
  if (!characterLock.trim()) problems.push('the character description');
  if (!settingLock.trim()) problems.push('the home / setting description');
  if (!prompt.trim()) problems.push('the persona');
  if (filledLocations.length < MIN_LOCATIONS)
    problems.push(`at least ${MIN_LOCATIONS} complete locations (name + photo link)`);
  const badLocationUrls = locations.filter((l) => l.url.trim() && !looksLikeUrl(l.url));
  if (badLocationUrls.length) problems.push('valid links on every location (must start with http)');
  const emails = emailsText
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  if (isPrivate && emails.length === 0) problems.push('at least one email for a private avatar');

  const canSubmit = problems.length === 0 && !busy && !prefilling;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // Resolve emails → user accounts first, so a typo fails before anything
      // is written.
      let allowedUsers: string[] = [];
      if (isPrivate) {
        const missing: string[] = [];
        for (const email of emails) {
          try {
            const u = await pb
              .collection('users')
              .getFirstListItem(`email = "${email.replace(/"/g, '')}"`);
            allowedUsers.push(u.id);
          } catch {
            missing.push(email);
          }
        }
        if (missing.length) {
          throw new Error(
            `No account found for: ${missing.join(', ')}. ` +
              'They need to sign into the app once (with Google) before they can be added.'
          );
        }
        // The creator should always keep access to their own private avatar.
        const creatorId = existing?.created_by || userId;
        if (creatorId && !allowedUsers.includes(creatorId)) allowedUsers.push(creatorId);
      }

      const fields = {
        name: name.trim(),
        gender,
        base_image_url: baseImageUrl.trim(),
        avatar_url: avatarUrl.trim() || baseImageUrl.trim(),
        character_lock: characterLock.trim(),
        setting_lock: settingLock.trim(),
        prompt: prompt.trim(),
        guidelines: guidelines.trim(),
        allowed_users: allowedUsers,
      };

      let influencerId = existing?.id ?? createdId;
      if (existing) {
        await pb.collection('influencers').update(existing.id, fields);
      } else if (!influencerId) {
        const record = await pb.collection('influencers').create({
          ...fields,
          influencer_id: `${slugify(name) || 'avatar'}_${Math.random().toString(36).slice(2, 6)}`,
          created_by: userId,
          active: true,
        });
        influencerId = record.id;
        setCreatedId(record.id);
      }

      // Rows removed in the editor. Deleting a location is safe for future
      // campaigns only — already-generated scenes keep their tag as text.
      for (const removedId of removedLocationIds) {
        await pb.collection('locations').delete(removedId).catch(() => {});
      }

      const usedTags = new Set<string>(
        locations.map((l) => l.tag).filter((t): t is string => Boolean(t))
      );
      for (let i = 0; i < locations.length; i++) {
        const row = locations[i]!;
        if (!row.name.trim() || !row.url.trim()) continue;

        if (row.id) {
          // Existing room: keep its tag stable — scenes and the image library
          // reference rooms by tag, so renaming the display name is fine but
          // the tag never changes.
          await pb.collection('locations').update(row.id, {
            name: row.name.trim(),
            usage_hint: row.hint?.trim() ?? '',
            drive_url: row.url.trim(),
            sort: i + 1,
          });
          continue;
        }

        if (savedLocations.has(i)) continue;
        let tag = slugify(row.name) || `room_${i + 1}`;
        while (usedTags.has(tag)) tag = `${tag}_2`;
        usedTags.add(tag);
        await pb.collection('locations').create({
          influencer: influencerId,
          name: row.name.trim(),
          tag,
          usage_hint: row.hint?.trim() ?? '',
          drive_url: row.url.trim(),
          is_global: false,
          sort: i + 1,
        });
        setSavedLocations((done) => new Set(done).add(i));
      }

      onCreated(name.trim());
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CreatorModal
      title={editing ? `Edit ${existing?.name || 'avatar'}` : 'Create a new avatar'}
      subtitle={
        prefilling
          ? 'Loading the avatar’s current details…'
          : 'Hover the ⓘ icons to see what each field does, with a good example.'
      }
      onClose={onClose}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="zen-label">
              Name *
              <FieldHint {...HINTS.name} />
            </label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="zen-input" placeholder="e.g. Yang" />
          </div>
          <div>
            <label className="zen-label">
              Gender *
              <FieldHint {...HINTS.gender} />
            </label>
            <div className="flex gap-1 p-1 bg-stone-100/80 rounded-xl">
              {(['female', 'male'] as const).map((g) => (
                <button
                  key={g}
                  onClick={() => setGender(g)}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer capitalize ${
                    gender === g ? 'bg-white shadow-xs' : 'text-natural-text/60'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="zen-label">
              Character photo link *
              <FieldHint {...HINTS.baseImage} />
            </label>
            <div className="flex items-center gap-2">
              <input
                value={baseImageUrl}
                onChange={(e) => setBaseImageUrl(e.target.value)}
                className="zen-input text-xs"
                placeholder="Drive link or direct image link"
              />
              {looksLikeUrl(baseImageUrl) && (
                <ThumbWithPreview url={baseImageUrl} alt="Character" className="w-10 h-10" />
              )}
            </div>
          </div>
          <div>
            <label className="zen-label">
              Thumbnail link (optional)
              <FieldHint {...HINTS.avatarThumb} />
            </label>
            <div className="flex items-center gap-2">
              <input
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                className="zen-input text-xs"
                placeholder="Defaults to the character photo"
              />
              {looksLikeUrl(avatarUrl) && (
                <ThumbWithPreview url={avatarUrl} alt="Thumbnail" className="w-10 h-10" />
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="zen-label">
            Character description (what never changes) *
            <FieldHint {...HINTS.characterLock} />
          </label>
          <textarea
            value={characterLock}
            onChange={(e) => setCharacterLock(e.target.value)}
            rows={3}
            className="zen-input text-xs leading-relaxed resize-y"
            placeholder="Age range, hair, build, outfit — appearance only, no actions"
          />
        </div>

        <div>
          <label className="zen-label">
            Home / setting description *
            <FieldHint {...HINTS.settingLock} />
          </label>
          <textarea
            value={settingLock}
            onChange={(e) => setSettingLock(e.target.value)}
            rows={2}
            className="zen-input text-xs leading-relaxed resize-y"
            placeholder="The overall style all the rooms share"
          />
        </div>

        <div>
          <label className="zen-label">
            Persona (used when the AI writes scripts) *
            <FieldHint {...HINTS.prompt} />
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            className="zen-input text-xs leading-relaxed resize-y"
            placeholder="Who this person is, how they talk, what they care about"
          />
        </div>

        <div>
          <label className="zen-label">
            Script guidelines (optional)
            <FieldHint {...HINTS.guidelines} />
          </label>
          <textarea
            value={guidelines}
            onChange={(e) => setGuidelines(e.target.value)}
            rows={3}
            className="zen-input text-xs leading-relaxed resize-y"
            placeholder="Dos and don'ts for every script with this avatar"
          />
        </div>

        <div>
          <label className="zen-label">
            Locations — minimum {MIN_LOCATIONS} *
            <FieldHint {...HINTS.locations} />
          </label>
          <div className="space-y-2">
            {locations.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={row.name}
                  onChange={(e) => setLocation(i, { name: e.target.value })}
                  className="zen-input text-xs w-40 shrink-0"
                  placeholder={`Room ${i + 1} name`}
                  disabled={savedLocations.has(i)}
                />
                <input
                  value={row.hint ?? ''}
                  onChange={(e) => setLocation(i, { hint: e.target.value })}
                  className="zen-input text-xs w-56 shrink-0"
                  placeholder="When to use — e.g. explaining science, giving advice"
                  disabled={savedLocations.has(i)}
                />
                <input
                  value={row.url}
                  onChange={(e) => setLocation(i, { url: e.target.value })}
                  className="zen-input text-xs flex-1"
                  placeholder="Room photo — Drive link or direct image link"
                  disabled={savedLocations.has(i)}
                />
                {looksLikeUrl(row.url) && (
                  <ThumbWithPreview url={row.url} alt={row.name || `Room ${i + 1}`} className="w-9 h-9" />
                )}
                {locations.length > MIN_LOCATIONS && !savedLocations.has(i) && (
                  <button
                    onClick={() => {
                      const removed = locations[i];
                      if (removed?.id) setRemovedLocationIds((ids) => [...ids, removed.id!]);
                      setLocations((rows) => rows.filter((_, j) => j !== i));
                    }}
                    className="p-1.5 rounded-lg text-natural-text/40 hover:text-red-500 cursor-pointer shrink-0"
                    title="Remove this row"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => setLocations((rows) => [...rows, { name: '', url: '' }])}
            className="zen-btn-ghost mt-2 text-xs"
          >
            <Plus size={13} />
            Add another location
          </button>
        </div>

        <div className="p-4 bg-natural-bg rounded-xl border border-natural-border/60 space-y-3">
          <label className="zen-label mb-0">
            Who can use this avatar?
            <FieldHint {...HINTS.privacy} />
          </label>
          <div className="flex gap-1 p-1 bg-stone-100/80 rounded-xl max-w-sm">
            <button
              onClick={() => setIsPrivate(false)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                !isPrivate ? 'bg-white shadow-xs' : 'text-natural-text/60'
              }`}
            >
              <Globe size={12} />
              Everyone
            </button>
            <button
              onClick={() => setIsPrivate(true)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                isPrivate ? 'bg-white shadow-xs' : 'text-natural-text/60'
              }`}
            >
              <Lock size={12} />
              Private
            </button>
          </div>
          {isPrivate && (
            <div>
              <input
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                className="zen-input text-xs"
                placeholder="Emails, separated by commas — e.g. anna@theimpactbrands.com, tomas@theimpactbrands.com"
              />
              <p className="text-[11px] text-natural-text/45 mt-1.5">
                Only these people (plus you and admins) will see and use this avatar. Each person
                must have signed into the app at least once.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!canSubmit && !busy && problems.length > 0 && (
          <p className="text-[11px] text-natural-text/45">Still needed: {problems.join(', ')}.</p>
        )}

        <button onClick={submit} disabled={!canSubmit} className="zen-btn-primary w-full">
          {editing ? <Save size={15} /> : <UserPlus size={15} />}
          {busy
            ? 'Saving…'
            : editing
              ? 'Save changes'
              : createdId
                ? 'Retry saving'
                : 'Create avatar'}
        </button>
      </div>
    </CreatorModal>
  );
}
